import {
  put,
  race,
  take,
  call,
  takeEvery,
  select,
} from 'redux-saga/effects';
import ApiResource from '../../../../api-resources';
import {
  extractFirstJsonapiResourceFromAction,
  extractErrorsFromPayload,
} from '../../../../utils/json-api';
import { createSuccessActionType } from '../../../../utils/actions';
import {
  createAuthentication,
  CREATE_RESOURCE_AUTHENTICATION_ERROR,
} from '../../../../actions/auth';
import { setContainerLoadState } from '../../../../actions/container-load-state';
import { requestUserEmailConfirmation } from '../../../../sagas/signin';
import { resolveProfile } from '../../../../sagas/profile';
import { userSelector } from '../../../../selectors';
import {
  SIGNIN,
  SIGNIN_WITH_LINKEDIN,
  SIGNIN_ABORT,
  signinSuccess,
} from '../actions/signin';
import {
  handleSpecialUsersSignin,
  processAfterSignin,
} from './afterSignin';

/**
  * Authenticate against the Auth Endpoint using whatever flavor
  * @param { Object } authCreds - (see actions/auth)
  * @param { Object } authOptions
  * @param { String } authOptions.flavor - linkedin/vanilla/identity
  * @param { String } authOptions.authFlow - signin/signup
  */
export function* authenticate(
  authCreds,
  {
    flavor = 'vanilla',
    authFlow = 'signin',
  }
) {
  yield put(createAuthentication(flavor, authFlow, authCreds));

  const {
    credentials,
    identity,
    error,
    abort,
  } = yield race({
    credentials: take(createSuccessActionType('CREATE', ApiResource.CREDENTIALS)),
    identity: take(createSuccessActionType('CREATE', ApiResource.IDENTITY)),
    error: take(CREATE_RESOURCE_AUTHENTICATION_ERROR),
    abort: take([SIGNIN_ABORT]),
  });

  return {
    credentials: extractFirstJsonapiResourceFromAction(credentials, 'credentials'),
    identity: extractFirstJsonapiResourceFromAction(identity, 'identity'),
    error,
    abort,
  };
}

/**
 * Perform sign-in flow
 * @param {Object} signinAttempParams
 * code, context, resolve, reject, type
 */
function* handleSignin(signinAttempParams) {
  yield put(setContainerLoadState('signin', { loading: true }));

  const { code, resolve, reject } = signinAttempParams;
  let { credentials, identity, error, abort } = yield call(authenticate, signinAttempParams, {
    flavor: (code ? 'linkedin' : 'vanilla'),
    authFlow: 'signin',
  });

  if (abort) {
    return;
  }

  if (error) {
    yield put(setContainerLoadState('signin', { error }));
    let errors = extractErrorsFromPayload(error);
    switch (errors[0]?.name) {
      case 'not_confirmed':
        yield call(requestUserEmailConfirmation);
        break;
      default:
        break;
    }
    if (typeof reject === 'function') {
      reject(error.error);
    }
  }

  if (identity) {
    yield put(setContainerLoadState('signin', { error }));
    if (typeof reject === 'function') {
      reject({
        __error: 'not_linkedin_connected',
      });
    }
    return;
  }

  //success
  if (credentials) {
    const user = yield select(userSelector);
    yield call(handleSpecialUsersSignin, signinAttempParams, user); // RH/MJG Admins

    const getProfileResult = yield call(resolveProfile, user);

    if (getProfileResult.success) {
      yield put(signinSuccess({ flow: user.type }));
      yield call(processAfterSignin, { user, context: signinAttempParams?.context });
    }
    if (typeof reject === 'function') {
      resolve();
    }
  }
}

/**
 *
 */
export function* hydrateSigninView() {
  yield takeEvery([SIGNIN, SIGNIN_WITH_LINKEDIN], handleSignin);
}
